import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Slider } from '@/components/ui/slider';
import { Play, Pause } from 'lucide-react';
import { useEffect, useRef } from 'react';

const TONE = {
  amber:    { dot: '#D97706', text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  slate:    { dot: '#94A3B8', text: 'text-slate-600',   bg: 'bg-slate-50',   border: 'border-slate-200' },
  critical: { dot: '#DC2626', text: 'text-apex-critical', bg: 'bg-red-50',  border: 'border-red-200' },
  lens:     { dot: '#0D9488', text: 'text-apex-lens',   bg: 'bg-teal-50',    border: 'border-teal-200' },
};

export default function ExposureScrubber({ replay }) {
  const [day, setDay] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [playing, setPlaying] = useState(false);
  const total = replay?.total_days ?? 0;
  const events = replay?.events ?? [];
  const rafRef = useRef(null);

  // Auto-play loop — clears any explicit selection so day-based lookup wins again
  useEffect(() => {
    if (!playing) return undefined;
    let last = performance.now();
    const tick = (now) => {
      const dt = now - last;
      last = now;
      setDay((d) => {
        const next = d + (dt / 1000) * (total / 6); // ~6 seconds full sweep
        if (next >= total) { setPlaying(false); return total; }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [playing, total]);

  const activeEvent = useMemo(() => {
    if (!events.length) return null;
    if (selectedIdx != null && events[selectedIdx]) return events[selectedIdx];
    return [...events].reverse().find((e) => e.day <= day) ?? events[0];
  }, [day, events, selectedIdx]);

  const startDate = new Date(replay?.start ?? Date.now());
  const currentDate = new Date(startDate.getTime() + day * 86400000);

  return (
    <div data-testid="exposure-scrubber" className="bg-white rounded-lg border border-slate-200 p-4 mt-3">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-apex-ink">{replay.title}</h4>
          <p className="text-[11px] text-apex-neutral mt-0.5 font-mono">
            {replay.start} → {replay.end} · {total} days
          </p>
        </div>
        <button
          onClick={() => { if (day >= total) setDay(0); setSelectedIdx(null); setPlaying(p => !p); }}
          data-testid="scrubber-play"
          className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-apex-lens text-apex-lens hover:bg-apex-lens hover:text-white transition-colors"
        >
          {playing ? <><Pause className="w-3 h-3" />Pause</> : <><Play className="w-3 h-3" />Replay</>}
        </button>
      </div>

      {/* Timeline track */}
      <div className="relative h-12 mb-1">
        {/* Hidden full-width range input — sits BEHIND dots so dots win clicks */}
        <input
          type="range"
          min={0}
          max={total}
          step={1}
          value={Math.round(day)}
          onChange={(e) => { setPlaying(false); setSelectedIdx(null); setDay(Number(e.target.value)); }}
          data-testid="scrubber-input"
          className="absolute inset-x-0 top-0 z-0 h-12 w-full opacity-0 cursor-pointer"
          aria-label="Exposure day scrubber"
        />
        {/* Track line */}
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-slate-100 rounded-full pointer-events-none" />
        {/* Filled portion up to current day */}
        <motion.div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-apex-lens rounded-full pointer-events-none"
          initial={false}
          animate={{ width: `${(day / total) * 100}%` }}
          transition={{ duration: 0.18, ease: 'linear' }}
        />
        {/* Event dots — z-10 so they always win pointer hit-testing */}
        {events.map((e, i) => {
          const t = TONE[e.tone] ?? TONE.slate;
          const left = `${(e.day / total) * 100}%`;
          const reached = day >= e.day;
          const active = activeEvent && events.indexOf(activeEvent) === i;
          return (
            <button
              key={i}
              onClick={() => { setPlaying(false); setSelectedIdx(i); setDay(e.day); }}
              data-testid={`scrubber-event-${i}`}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 group"
              style={{ left }}
              title={`Day ${e.day} · ${e.label}`}
            >
              <span
                className={`block rounded-full transition-all ${active ? 'w-4 h-4' : 'w-2.5 h-2.5'}`}
                style={{
                  backgroundColor: reached ? t.dot : '#CBD5E1',
                  boxShadow: active ? `0 0 0 4px ${t.dot}33` : 'none',
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Date axis */}
      <div className="flex justify-between text-[9px] font-mono text-apex-neutral mb-3">
        <span>{replay.start}</span>
        <span>·</span>
        <span>{replay.end}</span>
      </div>

      {/* Active event card */}
      {activeEvent && (
        <motion.div
          key={`${activeEvent.day}-${activeEvent.label}`}
          initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          data-testid="scrubber-active-event"
          className={`rounded-md border p-2.5 ${TONE[activeEvent.tone]?.bg} ${TONE[activeEvent.tone]?.border}`}
        >
          <div className="flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className={`text-[11px] font-semibold ${TONE[activeEvent.tone]?.text} truncate`}>{activeEvent.label}</div>
              <div className="text-[10px] font-mono text-apex-neutral mt-0.5">{activeEvent.ts}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-mono">Day</div>
              <div className="text-sm font-bold tabular text-apex-ink">{activeEvent.day} / {total}</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Live cursor date */}
      <div className="mt-2 text-[10px] font-mono text-apex-neutral text-right">
        Cursor: <span className="text-apex-ink-2">{currentDate.toISOString().slice(0, 10)}</span> · day <span className="text-apex-ink-2 tabular">{Math.round(day)}</span>
      </div>
    </div>
  );
}
